/**
 * Supabase Sync Layer
 *
 * Bridges localStorage ↔ Supabase. Handles:
 * - Uploading localStorage data to Supabase on first login ("claim" flow)
 * - Downloading Supabase data to localStorage on login
 * - Syncing writes to Supabase (debounced)
 */

import { supabase } from '../../auth/supabaseClient';
import storageService from './storageService';

// Debounce timers per key
const syncTimers = {};
const DEBOUNCE_MS = 1000;

/**
 * Namespace-to-table mapping for Supabase.
 * Maps our localStorage namespace:key to Supabase table + query shape.
 */
const SYNC_MAP = {
  'flashcards:session-history': {
    table: 'flashcard_sessions',
    type: 'array',
  },
  'cmddrill:sessions': {
    table: 'drill_sessions',
    type: 'array',
  },
  'examsim:exams': {
    table: 'exam_records',
    type: 'array',
  },
};

/**
 * Sync a write to Supabase (debounced).
 * Called after localStorage writes for authenticated users.
 */
export function queueSync(namespace, key, value, userId) {
  if (!supabase || !userId) return;

  const syncKey = `${namespace}:${key}`;
  clearTimeout(syncTimers[syncKey]);

  syncTimers[syncKey] = setTimeout(async () => {
    try {
      await syncToSupabase(namespace, key, value, userId);
    } catch (err) {
      console.warn(`Sync failed for ${syncKey}:`, err.message);
    }
  }, DEBOUNCE_MS);
}

/**
 * Upload flashcard progress to Supabase.
 * Upserts per-card SR data and stats.
 */
export async function syncFlashcardProgress(cards, userId) {
  if (!supabase || !userId) return;

  const progressRows = cards
    .filter(c => c.stats.times_seen > 0) // Only sync cards that have been studied
    .map(c => ({
      user_id: userId,
      card_id: c.id,
      easiness_factor: c.sr_data.easiness_factor,
      interval_days: c.sr_data.interval_days,
      repetitions: c.sr_data.repetitions,
      next_review_date: c.sr_data.next_review_date,
      last_review_date: c.sr_data.last_review_date,
      times_seen: c.stats.times_seen,
      times_correct: c.stats.times_correct,
      streak: c.stats.streak,
      updated_at: new Date().toISOString(),
    }));

  if (progressRows.length === 0) return;

  const { error } = await supabase
    .from('flashcard_progress')
    .upsert(progressRows, { onConflict: 'user_id,card_id' });

  if (error) console.warn('Flashcard progress sync error:', error.message);
}

/**
 * Upload drill question stats to Supabase.
 */
export async function syncDrillProgress(questions, userId) {
  if (!supabase || !userId) return;

  const progressRows = questions
    .filter(q => q.stats.times_attempted > 0)
    .map(q => ({
      user_id: userId,
      question_id: q.id,
      times_attempted: q.stats.times_attempted,
      times_correct: q.stats.times_correct,
      avg_time_seconds: q.stats.avg_time_seconds,
      best_time_seconds: q.stats.best_time_seconds,
      is_starred: q.is_starred || false,
      updated_at: new Date().toISOString(),
    }));

  if (progressRows.length === 0) return;

  const { error } = await supabase
    .from('drill_progress')
    .upsert(progressRows, { onConflict: 'user_id,question_id' });

  if (error) console.warn('Drill progress sync error:', error.message);
}

/**
 * Upload exam question stats to Supabase.
 */
export async function syncExamQuestionStats(questions, userId) {
  if (!supabase || !userId) return;

  const statsRows = questions
    .filter(q => q.stats.times_seen > 0)
    .map(q => ({
      user_id: userId,
      question_id: q.id,
      times_seen: q.stats.times_seen,
      times_correct: q.stats.times_correct,
      avg_time_seconds: q.stats.avg_time_seconds,
      updated_at: new Date().toISOString(),
    }));

  if (statsRows.length === 0) return;

  const { error } = await supabase
    .from('exam_question_stats')
    .upsert(statsRows, { onConflict: 'user_id,question_id' });

  if (error) console.warn('Exam stats sync error:', error.message);
}

/**
 * Sync a session/exam record to Supabase.
 */
async function syncToSupabase(namespace, key, value, userId) {
  const mapEntry = SYNC_MAP[`${namespace}:${key}`];
  if (!mapEntry) return;

  // For array records (sessions, exams), upsert the latest entry
  if (mapEntry.type === 'array' && Array.isArray(value) && value.length > 0) {
    const latest = value[value.length - 1];
    const row = { ...latest, user_id: userId };
    // Remove any fields that don't match the table schema
    delete row.id; // Let Supabase generate UUID

    const { error } = await supabase
      .from(mapEntry.table)
      .insert(row);

    if (error && !error.message.includes('duplicate')) {
      console.warn(`Sync insert to ${mapEntry.table}:`, error.message);
    }
  }
}

/**
 * Download user data from Supabase on login.
 * Merges Supabase data into localStorage (Supabase wins for conflicts).
 */
export async function downloadUserData(userId) {
  if (!supabase || !userId) return;

  try {
    // Download flashcard progress
    const { data: fcProgress } = await supabase
      .from('flashcard_progress')
      .select('*')
      .eq('user_id', userId);

    if (fcProgress && fcProgress.length > 0) {
      const cards = storageService.get('flashcards', 'all', []);
      const progressMap = Object.fromEntries(fcProgress.map(p => [p.card_id, p]));

      const merged = cards.map(card => {
        const progress = progressMap[card.id];
        if (!progress) return card;
        return {
          ...card,
          sr_data: {
            easiness_factor: progress.easiness_factor,
            interval_days: progress.interval_days,
            repetitions: progress.repetitions,
            next_review_date: progress.next_review_date,
            last_review_date: progress.last_review_date,
          },
          stats: {
            times_seen: progress.times_seen,
            times_correct: progress.times_correct,
            streak: progress.streak,
          },
        };
      });
      storageService.set('flashcards', 'all', merged);
    }

    // Download drill progress
    const { data: drillProgress } = await supabase
      .from('drill_progress')
      .select('*')
      .eq('user_id', userId);

    if (drillProgress && drillProgress.length > 0) {
      const questions = storageService.get('cmddrill', 'questions', []);
      const progressMap = Object.fromEntries(drillProgress.map(p => [p.question_id, p]));

      const merged = questions.map(q => {
        const progress = progressMap[q.id];
        if (!progress) return q;
        return {
          ...q,
          stats: {
            times_attempted: progress.times_attempted,
            times_correct: progress.times_correct,
            avg_time_seconds: progress.avg_time_seconds,
            best_time_seconds: progress.best_time_seconds,
          },
          is_starred: progress.is_starred,
        };
      });
      storageService.set('cmddrill', 'questions', merged);
    }

    // Download exam records
    const { data: examRecords } = await supabase
      .from('exam_records')
      .select('*')
      .eq('user_id', userId);

    if (examRecords && examRecords.length > 0) {
      const existing = storageService.get('examsim', 'exams', []);
      const existingIds = new Set(existing.map(e => e.id));
      const newRecords = examRecords.filter(e => !existingIds.has(e.id));
      if (newRecords.length > 0) {
        storageService.set('examsim', 'exams', [...existing, ...newRecords]);
      }
    }

    // Update last_active_at
    await supabase
      .from('profiles')
      .update({ last_active_at: new Date().toISOString() })
      .eq('id', userId);

  } catch (err) {
    console.warn('Download user data error:', err.message);
  }
}

/**
 * Upload all localStorage data to Supabase ("claim" flow).
 * Used when user has existing localStorage data but no Supabase data.
 */
export async function uploadLocalData(userId) {
  if (!supabase || !userId) return;

  try {
    const cards = storageService.get('flashcards', 'all', []);
    await syncFlashcardProgress(cards, userId);

    const questions = storageService.get('cmddrill', 'questions', []);
    await syncDrillProgress(questions, userId);

    const examQuestions = storageService.get('examsim', 'questions', []);
    await syncExamQuestionStats(examQuestions, userId);

    // Upload session histories
    const flashSessions = storageService.get('flashcards', 'session-history', []);
    for (const session of flashSessions) {
      await supabase.from('flashcard_sessions').insert({ ...session, user_id: userId }).select();
    }

    const drillSessions = storageService.get('cmddrill', 'sessions', []);
    for (const session of drillSessions) {
      await supabase.from('drill_sessions').insert({ ...session, user_id: userId }).select();
    }

    const exams = storageService.get('examsim', 'exams', []);
    for (const exam of exams) {
      await supabase.from('exam_records').insert({ ...exam, user_id: userId }).select();
    }

  } catch (err) {
    console.warn('Upload local data error:', err.message);
  }
}

/**
 * Get leaderboard data, optionally scoped to a team.
 */
export async function getLeaderboard(teamId) {
  if (!supabase) return [];

  try {
    if (teamId) {
      const { data, error } = await supabase.rpc('get_team_leaderboard', { p_team_id: teamId });
      if (error) throw error;
      return data || [];
    }
    // Fallback to global leaderboard (backward compat)
    const { data, error } = await supabase.rpc('get_leaderboard');
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.warn('Leaderboard fetch error:', err.message);
    return [];
  }
}

/**
 * Get team members for a specific team.
 */
export async function getTeamMembers(teamId) {
  if (!supabase) return [];

  try {
    if (teamId) {
      // Get memberships for this team
      const { data: memberships, error: memError } = await supabase
        .from('team_members')
        .select('user_id, role, joined_at')
        .eq('team_id', teamId)
        .order('joined_at', { ascending: true });
      if (memError) throw memError;
      if (!memberships || memberships.length === 0) return [];

      // Get profile details for those members
      const userIds = memberships.map(m => m.user_id);
      const { data: profiles, error: profError } = await supabase
        .from('profiles')
        .select('id, email, display_name, last_active_at, created_at')
        .in('id', userIds);
      if (profError) throw profError;

      // Merge membership data into profiles
      const profileMap = Object.fromEntries((profiles || []).map(p => [p.id, p]));
      return memberships
        .map(tm => ({
          ...profileMap[tm.user_id],
          role: tm.role,
          joined_at: tm.joined_at,
        }))
        .filter(m => m.id); // skip if profile not found
    }
    // Fallback: all profiles (backward compat)
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.warn('Team members fetch error:', err.message);
    return [];
  }
}

/**
 * Update a member's role within a team.
 */
export async function updateTeamMemberRole(teamId, userId, newRole) {
  if (!supabase) return { error: { message: 'Supabase not configured' } };

  const { data, error } = await supabase
    .from('team_members')
    .update({ role: newRole })
    .eq('team_id', teamId)
    .eq('user_id', userId)
    .select()
    .single();

  return { data, error };
}

/**
 * Legacy: update a user's role on profiles table (backward compat).
 */
export async function updateUserRole(userId, newRole) {
  if (!supabase) return { error: { message: 'Supabase not configured' } };

  const { data, error } = await supabase
    .from('profiles')
    .update({ role: newRole })
    .eq('id', userId)
    .select()
    .single();

  return { data, error };
}

// ============================================================
// TEAM CRUD
// ============================================================

/**
 * Get all teams the current user belongs to.
 */
export async function getUserTeams(userId) {
  if (!supabase || !userId) return [];

  try {
    // Get memberships first
    const { data: memberships, error: memError } = await supabase
      .from('team_members')
      .select('team_id, role')
      .eq('user_id', userId);

    if (memError) throw memError;
    if (!memberships || memberships.length === 0) return [];

    // Get team details for those memberships
    const teamIds = memberships.map(m => m.team_id);
    const { data: teams, error: teamError } = await supabase
      .from('teams')
      .select('id, name, invite_code, created_by, created_at')
      .in('id', teamIds);

    if (teamError) throw teamError;

    // Merge role into team objects
    const roleMap = Object.fromEntries(memberships.map(m => [m.team_id, m.role]));
    return (teams || []).map(t => ({
      ...t,
      role: roleMap[t.id],
    }));
  } catch (err) {
    console.warn('Get user teams error:', err.message);
    return [];
  }
}

/**
 * Create a new team. The creator becomes admin.
 */
export async function createTeam(name, userId) {
  if (!supabase || !userId) return { data: null, error: { message: 'Not configured' } };

  try {
    const { data, error } = await supabase.rpc('create_team', { p_name: name });
    if (error) throw error;
    return { data, error: null };
  } catch (err) {
    console.warn('Create team error:', err.message);
    return { data: null, error: err };
  }
}

/**
 * Join a team using an invite code.
 */
export async function joinTeamByCode(inviteCode) {
  if (!supabase) return { data: null, error: { message: 'Not configured' } };

  try {
    const { data, error } = await supabase.rpc('join_team_by_code', {
      p_invite_code: inviteCode.trim(),
    });
    if (error) throw error;
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err };
  }
}

/**
 * Leave a team (remove self).
 */
export async function leaveTeam(teamId, userId) {
  if (!supabase) return { error: { message: 'Not configured' } };

  const { error } = await supabase
    .from('team_members')
    .delete()
    .eq('team_id', teamId)
    .eq('user_id', userId);

  return { error };
}

/**
 * Remove a member from a team (admin action).
 */
export async function removeTeamMember(teamId, userId) {
  if (!supabase) return { error: { message: 'Not configured' } };

  const { error } = await supabase
    .from('team_members')
    .delete()
    .eq('team_id', teamId)
    .eq('user_id', userId);

  return { error };
}

/**
 * Regenerate a team's invite code (admin only).
 */
export async function regenerateInviteCode(teamId) {
  if (!supabase) return { data: null, error: { message: 'Not configured' } };

  try {
    const { data, error } = await supabase.rpc('regenerate_invite_code', {
      p_team_id: teamId,
    });
    if (error) throw error;
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err };
  }
}
